import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NetworkConfig } from '../modules/admin/interface/NetworkConfig';

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  private _isElectron = new BehaviorSubject<boolean>(false);
  private _isCapacitor = new BehaviorSubject<boolean>(false);
  private _isLocalNetwork = new BehaviorSubject<boolean>(false);
  private _isCloud = new BehaviorSubject<boolean>(false);
  private _apiUrl = new BehaviorSubject<string>(environment.ASquareAPIURL);

  isElectron$ = this._isElectron.asObservable();
  isCapacitor$ = this._isCapacitor.asObservable();
  isLocalNetwork$ = this._isLocalNetwork.asObservable();
  isCloud$ = this._isCloud.asObservable();
  apiUrl$ = this._apiUrl.asObservable();

  constructor() {
    this.initializeConfig();
  }

  /** Detect platform (Electron, Capacitor, or Web) */
  private detectPlatform(): void {
    const win = window as any;
    const isElectron = !!(
      win?.process?.type === 'renderer' ||
      (typeof win?.require === 'function' && win.require('electron'))
    );
    this._isElectron.next(isElectron);
    this._isCapacitor.next(!!win.Capacitor?.isNativePlatform());
  }

  /** Detect network type and modify `environment.apiUrl` */
  async initializeConfig(): Promise<void> {
    this.detectPlatform();

    const isDevMode = !environment.production;
    const networkConfig = await this.getStoredLocalApiIp();

    if(isDevMode) {
      this._isLocalNetwork.next(true);
      this._isCloud.next(false);
      environment.ASquareAPIURL = `http://localhost:5125/`;
      // environment.ASquareAPIURL = `https://a-square-erp-service.azurewebsites.net/`;
    } else if (networkConfig.ServerIP !== null && networkConfig.ServerIP !== '') {
      this._isLocalNetwork.next(true);
      this._isCloud.next(false);
      environment.ASquareAPIURL = `http://${networkConfig.ServerIP}:5125/`; // Local API URL
    } else {
      this._isLocalNetwork.next(false);
      this._isCloud.next(true);
      environment.ASquareAPIURL = 'https://a-square-erp-service-a-square-erp-service-prod.azurewebsites.net/'; // Cloud API URL
      // environment.ASquareAPIURL = `https://a-square-erp-service.azurewebsites.net/`;
    }

    this._apiUrl.next(environment.ASquareAPIURL);

    console.log(`Running in: ${this._isElectron.getValue() ? 'Electron' : this._isCapacitor.getValue() ? 'Capacitor' : 'Browser'}`);
    console.log(`Network Type: ${this._isLocalNetwork.getValue() ? 'Local Network' : 'Cloud'}`);
    console.log(`Updated API URL: ${environment.ASquareAPIURL}`);
  }

  get isElectron(): boolean { // For immediate/sync access
    return this._isElectron.value;
  }

  get isCapacitor(): boolean {
    return this._isCapacitor.value;
  }

  get isLocalNetwork(): boolean {
    return this._isLocalNetwork.value;
  }

  getCloudApiUrl(): string {
    return environment.production
      ? 'https://a-square-erp-service-a-square-erp-service-prod.azurewebsites.net/'
      : 'https://a-square-erp-service.azurewebsites.net/';
  }

  /** Save user-provided local API IP */
  async setLocalApiIp(networkConfig: NetworkConfig): Promise<void> {
    if(this._isCapacitor.getValue()) {
      await Preferences.set({ key: 'localApiIp', value: networkConfig.ServerIP });
    } else {
      localStorage.setItem('localApiIp', networkConfig.ServerIP);
    }

    await this.initializeConfig(); // Recalculate API URL
  }

  /** Get stored local API IP */
  async getStoredLocalApiIp(): Promise<NetworkConfig> {
    if (this._isCapacitor.getValue()) {
      let config: NetworkConfig = {
        ServerIP: (await Preferences.get({ key: 'localApiIp' })).value
      };

      return config;
    } else {
      let config: NetworkConfig = {
        ServerIP: localStorage.getItem('localApiIp')
      };

      return config;
    }
  }
}
